import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigationManager } from '@provider/navigation';
import { useUserIdentityState } from '@provider/user-identity';
import { SECONDS_TO_MS, DAYS_TO_MS } from '@utils/constants';
import { useGetRevisionFromURLQuery } from './query.get-from-url.js';
import { getAllRevisionsRequest } from './request.get-all.js';

const getAllRevisionsQueryKey = (user, courseId) => ["course-revisions/get-all", user?.id, courseId];
const getAllRevisionsQuery = (user, courseId) => {
  return {
    queryKey: getAllRevisionsQueryKey(user, courseId ?? ""),
    queryFn: () => getAllRevisionsRequest(user, courseId),
    staleTime: 15 * SECONDS_TO_MS,
    gcTime: 7 * DAYS_TO_MS,
    meta: {
      persist: false
    }
  };
};
const useGetAllRevisionsQuery = (courseId, extraConfig) => {
  //
  // STEP: load the user's own revisions into memory
  //
  const urlRevision = useGetRevisionFromURLQuery();
  const user = useUserIdentityState(state => state.user);
  const userRevisions = useQuery({
    ...getAllRevisionsQuery(user, courseId),
    ...extraConfig
  });

  //
  // STEP: push the currently selected revisionId to the URL query string
  // parameter
  //
  const location = useLocation();
  const navigationManager = useNavigationManager();
  useEffect(() => {
    const revisionId = new URL(location.asPath, "http://localhost/").searchParams.get("revisionId");
    if (courseId != null && urlRevision.data?.id === revisionId && urlRevision.data?.courseId !== courseId) {
      // remove revisionId from query if the fetched revision does not belong
      // to this specific course
      const url = new URL(navigationManager.getLocation().asPath, "http://localhost/");
      if (url.searchParams.get("revisionId") === revisionId) {
        url.searchParams.set("revisionId", "");
        url.searchParams.set("revisionTitle", "");
      }
      navigationManager.replace(url.pathname + url.search + url.hash);
    }
  }, [urlRevision.data, urlRevision.isPending, navigationManager, courseId, location.asPath]);
  return useMemo(() => {
    const revisionId = new URL(location.asPath, "http://localhost/").searchParams.get("revisionId");
    if (!urlRevision.data) {
      return userRevisions;
    }
    if (!userRevisions.data) {
      return userRevisions;
    }
    if (userRevisions.data.some(it => it.id === urlRevision.data?.id)) {
      return userRevisions;
    }
    const isUrlRevisionActive = urlRevision.data.id === revisionId;
    return {
      ...userRevisions,
      data: [{
        ...urlRevision.data,
        isActive: isUrlRevisionActive
      }, ...userRevisions.data.map(it => ({
        ...it,
        isActive: isUrlRevisionActive ? false : it.isActive
      }))]
    };
  }, [location.asPath, urlRevision.data, userRevisions]);
};

export { getAllRevisionsQuery, getAllRevisionsQueryKey, useGetAllRevisionsQuery };
//# sourceMappingURL=query.get-all.js.map
